import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@dsny/dsny-component-library';
import { Keyboardarrowright } from '@dsny/dsny-component-library/icons';
import { MultiCompanyAccessCompany } from 'src/features/MultiCompanyAccess';
import {
  ArrowWrapper,
  CompanyList,
  CompanyName,
  CompanyRow,
  ModalContent,
  ModalTitle,
  NoResultsRow,
  SearchInputStyles,
  SearchTailingStyles,
  SearchWrapper,
  SearchWrapperStyles,
  TextFieldWrapper,
} from './CompanySelectionMobileModal.styles';

interface CompanySelectionMobileModalProps {
  companies: MultiCompanyAccessCompany[];
  selectedCompanyId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectCompany: (company: MultiCompanyAccessCompany) => void;
}

const CompanySelectionMobileModal: React.FC<
  CompanySelectionMobileModalProps
> = ({ companies, selectedCompanyId, isOpen, onClose, onSelectCompany }) => {
  const { t } = useTranslation();
  const [searchWord, setSearchWord] = useState<string>('');

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchWord('');
    }
  }, [isOpen]);

  const filteredCompanies = useMemo(
    () =>
      companies.filter((company) =>
        company.business_name.toLowerCase().includes(searchWord.toLowerCase())
      ),
    [companies, searchWord]
  );

  // Handle search input changes
  const handleSearch = (event: React.SyntheticEvent): void => {
    const target = event.target as HTMLInputElement;
    setSearchWord(target.value);
  };

  // Handle company selection
  const handleSelect = (company: MultiCompanyAccessCompany): void => {
    onSelectCompany(company);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      containerStyles={{
        display: 'inline-flex',
        width: '440px',
        height: 'fit-content',
        borderRadius: '8px',
      }}
    >
      <ModalContent>
        <ModalTitle>{t('MULTI_COMPANY_TITLE')}</ModalTitle>

        <SearchWrapper>
          <TextFieldWrapper
            id="sidebar-company-search-mobile"
            name="sidebar-company-search-mobile"
            setting="mtr"
            labelText=""
            placeholder={t('MULTI_COMPANY_SEARCH_PLACEHOLDER')}
            width="100%"
            tailingIcon="Search"
            onChange={handleSearch as () => void}
            wrapperStyles={{ ...SearchWrapperStyles }}
            inputStyle={{ ...SearchInputStyles }}
            tailingStyle={{ ...SearchTailingStyles }}
          />
        </SearchWrapper>

        <CompanyList>
          {filteredCompanies.length > 0 ? (
            filteredCompanies.map((company) => (
              <CompanyRow
                key={company.company_id}
                $isSelected={company.company_id === selectedCompanyId}
                onClick={() => handleSelect(company)}
              >
                <CompanyName>{company.business_name}</CompanyName>
                <ArrowWrapper>
                  <Keyboardarrowright style={{ width: 16, height: 16 }} />
                </ArrowWrapper>
              </CompanyRow>
            ))
          ) : (
            <NoResultsRow>{t('MULTI_COMPANY_NO_RESULTS')}</NoResultsRow>
          )}
        </CompanyList>
      </ModalContent>
    </Modal>
  );
};

export default CompanySelectionMobileModal;
